import * as React from 'react';
import { View, TouchableOpacity } from 'react-native';
import { Text, Icon } from 'react-native-elements';
import { useFocusEffect } from '@react-navigation/native';
import moment from 'moment';
import { getProductsByCommerce } from 'services/productService';
import ShoppingCart from 'types/models/ShoppingCart';
import Product from 'types/models/Product';
import AppContext from 'contexts/AppContext';
import OrderContext from 'contexts/OrderContext';
import ShoppingScreenProps from 'types/screens/ShoppingScreenProps';
import ProductList from 'components/ProductList';
import SuccessModal from 'components/SuccessModal';
import OrderDetailModal from 'components/OrderDetailModal';
import useMediumScreen from 'hooks/useMediumScreen';
import useChangeAuthData from 'hooks/useChangeAuthData';
import useStoredProducts from 'hooks/useStoredProducts';
import useNotProcessedOrders from 'hooks/useNotProcessedOrders';
import { styles } from 'styles/screens/shoppingScreen';
import { useTranslation } from 'react-i18next';

export default ({ navigation }: ShoppingScreenProps): JSX.Element => {
  const [shoppingCart, setShoppingCart] = React.useState<ShoppingCart | null>(null);
  const [commerceProducts, setCommerceProducts] = React.useState<Product[] | null>(null);
  const [openShoppingModal, setOpenShoppingModal] = React.useState<boolean>(false);
  const { deleteAuthData } = useChangeAuthData();
  const { storeProducts, getStoredProducts } = useStoredProducts();
  const mediumScreen = useMediumScreen();
  const { processPendingOrders } = useNotProcessedOrders();
  const { setModalData, setModalizeData } = React.useContext(AppContext);
  const { currentCommerce, isConnected, currentVenue } = React.useContext(OrderContext);
  const currentDateTime = mediumScreen ? moment().format('DD/MM/YYYY hh:mm') : null;

  const { t } = useTranslation();

  const onChangeQuantity = (id: string, quantity: number) => {
    if (!shoppingCart) return;
    const detailItem = shoppingCart.detail.find((item) => item.product.id === id);
    if (detailItem) {
      detailItem.quantity = quantity;
    }
    setShoppingCart({ ...shoppingCart });
  };

  useFocusEffect(
    React.useCallback(() => {
      if (currentCommerce) {
        const getProducts = async () => {
          let products: Product[] | null;
          if (isConnected) {
            products = await getProductsByCommerce(currentCommerce?.id);
            await storeProducts(products);
          } else {
            products = await getStoredProducts();
          }
          if (products) {
            setCommerceProducts(products);
            const initialShoppingCart = {
              commerceId: currentCommerce.id,
              detail: [],
              isCash: true,
            };
            setShoppingCart({ ...initialShoppingCart });
          }
        };
        getProducts();
      } else {
        setTimeout(() => {
          navigation.reset({
            index: 0,
            routes: [{ name: 'VenueSelectionScreen' }],
          });
        }, 500);
      }
    }, [currentCommerce]),
  );

  React.useEffect(() => {
    const uploadingPendingOrders = async () => {
      await processPendingOrders();
    };
    uploadingPendingOrders();
  }, [isConnected]);

  const modalMessageContent = (message: string, successOrder: boolean, hasInternetConection: boolean): JSX.Element => (
    <SuccessModal
      isSuccess={successOrder}
      title={successOrder || !hasInternetConection ? t('confirmed-order') : t('confirmed-order-error')}
      mainColor={currentVenue?.appColor ?? '#001C47'}
      message={message}
      buttonText={successOrder || !hasInternetConection ? t('back-to-register-order') : t('back-to-try-order')}
      onConfirm={() => {
        setModalizeData(null);
        if (successOrder || !hasInternetConection) {
          navigation.reset({
            index: 0,
            routes: [{ name: 'ShoppingScreen' }],
          });
        } else {
          navigation.navigate('ShoppingScreen');
        }
      }}
      isMediumScreen={mediumScreen}
    />
  );

  const openMessageModal = (message: string, successOrder: boolean, hasInternetConection: boolean) => {
    setModalizeData({
      content: modalMessageContent(message, successOrder, hasInternetConection),
      closeOnOverlayTap: false,
    });
  };

  const handleProductsWithoutStock = (productsWithoutStock: Product[], successOrder: boolean) => {
    let messageMaximumUnitsAvailable = '';
    productsWithoutStock.map((product, index) => {
      if (productsWithoutStock.length > 1 && index === productsWithoutStock.length - 1) {
        messageMaximumUnitsAvailable += ` ${t('and-no-sufficient-stock-of')} ${product.name}`;
      } else if (productsWithoutStock.length > 2 && index !== productsWithoutStock.length - 2) {
        messageMaximumUnitsAvailable += `${t('no-sufficient-stock-of')} ${product.name}, `;
      } else if (product.stock === 0) {
        messageMaximumUnitsAvailable += `${t('no-stock-available-of')} ${product.name}`;
      } else if (product.stock === 0 && productsWithoutStock.length > 1 && index === productsWithoutStock.length - 1) {
        messageMaximumUnitsAvailable += ` ${t('and-no-stock-available-of')} ${product.name}`;
      } else if (product.stock === 0 && productsWithoutStock.length > 2 && index !== productsWithoutStock.length - 2) {
        messageMaximumUnitsAvailable += `${t('no-stock-available-of')} ${product.name}, `;
      } else if (index > 0 && index !== productsWithoutStock.length - 1) {
        messageMaximumUnitsAvailable += ` ${t('no-sufficient-stock-of')} ${product.name}`;
      } else if (product.stock === 0 && index > 0 && index !== productsWithoutStock.length - 1) {
        messageMaximumUnitsAvailable += ` ${t('no-stock-available-of')} ${product.name}`;
      } else {
        messageMaximumUnitsAvailable += `${t('no-sufficient-stock-of')} ${product.name}`;
      }
    });
    messageMaximumUnitsAvailable += '.';
    openMessageModal(messageMaximumUnitsAvailable, successOrder, isConnected);
  };

  if (!commerceProducts) return <></>;
  return (
    <View style={styles.container}>
      <View style={mediumScreen ? styles.header : styles.headerSmall}>
        <Icon
          name="arrow-back-outline"
          type="ionicon"
          color="#001C47"
          size={40}
          containerStyle={mediumScreen ? styles.goBackContainerLarge : styles.goBackContainerSmall}
          onPress={() => {
            if (navigation.canGoBack()) {
              navigation.goBack();
            } else {
              navigation.reset({
                index: 0,
                routes: [{ name: 'OrdersScreen' }],
              });
            }
          }}
        />
        {mediumScreen ? (
          <View style={styles.upperTextContainerLarge}>
            <Text style={styles.textTitleLargeOrders}>
              <Text
                style={[styles.textTitleLargeOrders, styles.textTitleLargeCommerce]}
              >{`${currentCommerce?.name} >`}</Text>
              {`${t('register-order')}`}
            </Text>
            <View style={{ marginTop: 25, marginLeft: 60 }}>
              <Text
                style={{
                  fontSize: 18,
                  fontWeight: '400',
                  lineHeight: 20,
                  letterSpacing: 0.25,
                  textAlign: 'left',
                  color: '#001639',
                }}
              >{`${currentDateTime}hs`}</Text>
            </View>
          </View>
        ) : (
          <Text style={mediumScreen ? styles.textLarge : styles.textSmall}>{currentCommerce?.name}</Text>
        )}
        <Icon
          name="person-circle-sharp"
          type="ionicon"
          color="#001639"
          size={60}
          containerStyle={mediumScreen ? styles.iconLarge : styles.iconSmall}
          onPress={() =>
            setModalData({
              description: t('logout'),
              generalColor: currentVenue?.appColor ?? '#001C47',
              action: async () => {
                await deleteAuthData();
              },
            })
          }
        />
      </View>
      {!mediumScreen && (
        <Text
          style={{
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignSelf: 'flex-start',
            marginLeft: 40,
            fontSize: 16,
            fontWeight: '500',
            lineHeight: 24,
            letterSpacing: 0,
            textAlign: 'left',
          }}
        >
          {t('register-order')}
        </Text>
      )}
      <ProductList
        products={commerceProducts}
        onChangeQuantity={onChangeQuantity}
        setCurrentShoppingCart={setShoppingCart}
        currentShoppingCart={shoppingCart}
        generalColor={currentVenue?.appColor ?? '#001C47'}
      />
      {shoppingCart && shoppingCart?.detail.length > 0 && (
        <>
          <View style={mediumScreen ? styles.openPaymentContainerLarge : styles.openPaymentContainerSmall}>
            <TouchableOpacity onPress={() => setOpenShoppingModal(true)}>
              <Icon
                name={'chevron-up'}
                type="entypo"
                color={currentVenue?.appColor}
                style={mediumScreen ? styles.iconLarge : styles.iconSmall}
                size={mediumScreen ? 40 : 35}
                containerStyle={styles.containerIcon}
              />
              <View style={styles.paymentSignalContainer}>
                <Text style={[styles.goToPaymentText, { color: currentVenue?.appColor ?? '#001C47' }]}>
                  {t('go-to-payment')}
                </Text>
              </View>
            </TouchableOpacity>
          </View>
          <OrderDetailModal
            shoppingCart={shoppingCart}
            openMessageModal={openMessageModal}
            handleProductsWithoutStock={handleProductsWithoutStock}
            openShoppingModal={openShoppingModal}
            setOpenShoppingModal={setOpenShoppingModal}
            generalColor={currentVenue?.appColor ?? '#001C47'}
          />
        </>
      )}
    </View>
  );
};
